import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../../hooks/useAppContext';
import { Role, ReportType } from '../../types';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { Button } from '../ui/Button';
import useAppStore from '../../store/useAppStore';

interface AccessGuardProps {
  requiredType?: ReportType;
  requiresPackageManagement?: boolean;
  requiresImportExport?: boolean;
  requiresTeamLead?: boolean;
  requiresAccounting?: boolean;
  requiresPurchaseManagement?: boolean;
  children: React.ReactNode;
}

const AccessGuard: React.FC<AccessGuardProps> = ({ requiredType, requiresPackageManagement, requiresImportExport, requiresTeamLead, requiresAccounting, requiresPurchaseManagement, children }) => {
  const { user } = useAppContext();
  const { currentUserLedTeam } = useAppStore();
  const navigate = useNavigate();

  // Admins always allowed. Otherwise, check specific permission or allowed report types.
  const isAllowed = React.useMemo(() => {
    if (!user) return false;
    if (user.role === Role.Admin) return true;

    // Team leader (or developer who leads a team) gating
    if (requiresTeamLead) {
      // Easier rule: TeamLead gets access even without assigned team
      if (user.role === Role.TeamLead) return true;
      // Developers still need to lead a team to enter
      if (user.role === Role.Developer) return !!currentUserLedTeam;
      return false;
    }

    // Accounting section gating (Purchase Archiving)
    if (requiresAccounting) {
      // Allow Admins and Accounting role
      return user.role === Role.Accounting || user.role === Role.Admin;
    }

    // Package management gating
    if (requiresPackageManagement) {
      return !!user.hasPackageManagementPermission;
    }

    // Import/Export gating
    if (requiresImportExport) {
      return !!user.hasImportExportPermission;
    }

    // Purchase Management gating
    if (requiresPurchaseManagement) {
      return !!(user as any).hasPurchaseManagementPermission;
    }

    // Report type gating
    if (requiredType) {
      const allowed = user.allowedReportTypes || [];
      if (allowed.length === 0) return false;
      return allowed.includes(requiredType);
    }

    // If no specific requirement provided, deny by default
    return false;
  }, [user, requiredType, requiresPackageManagement, requiresImportExport, requiresTeamLead, requiresAccounting, requiresPurchaseManagement, currentUserLedTeam]);

  if (isAllowed) {
    return <>{children}</>;
  }

  return (
    <div className="p-6">
      <Card>
        <CardHeader>
          <CardTitle>غير مصرح لك بالوصول</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="mb-4">ليس لديك صلاحية للوصول إلى هذا المحتوى.</p>
          <Button onClick={() => navigate('/')}>الرجوع للصفحة الرئيسية</Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default AccessGuard;